/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useTranslation } from 'react-i18next'
import { marketingApex } from '@/lib/host'
import { cn } from '@/lib/utils'
import { useStatus } from '@/hooks/use-status'
import { useSystemConfig } from '@/hooks/use-system-config'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'

type SystemBrandProps = {
  defaultName?: string
  defaultVersion?: string
  /**
   * Visual layout:
   * - 'sidebar': stacked card style (used inside the sidebar header).
   * - 'inline': compact horizontal pill (used inside the top app bar).
   */
  variant?: 'sidebar' | 'inline'
}

/**
 * System brand component
 * Displays current system logo + name.
 * - inline: compact pill in the top app bar; clicking navigates to home (/)
 * - sidebar: stacked card in the sidebar header (display only)
 */
export function SystemBrand(props: SystemBrandProps) {
  const { t, i18n } = useTranslation()
  const { status } = useStatus()
  const { logo } = useSystemConfig()

  const variant = props.variant ?? 'sidebar'
  const name = status?.system_name || props.defaultName || 'New API'
  // version is shown as a title tooltip on the sidebar logo for collapsed state
  const version =
    status?.version || props.defaultVersion || t('Unknown version')

  // 4allapi.com 部署：左上角 logo 点击回 marketing 主站，带当前语言。
  // Tanstack <Link to='/'> 会被 SPA 内部 router 截留，所以这里用原生 <a> 强制
  // 触发浏览器导航 → nginx 处理 → 主站显示对应语言版本。
  const homeUrlFor = (lng?: string) => {
    const raw = (lng || '').toLowerCase()
    const lang = raw.startsWith('ja')
      ? 'ja'
      : raw.startsWith('ko')
        ? 'ko'
        : raw.startsWith('en')
          ? 'en'
          : 'zh'
    return `https://${marketingApex()}/?lang=${lang}`
  }

  if (variant === 'inline') {
    return (
      <a
        href={homeUrlFor(i18n.language)}
        aria-label={t('Go to home')}
        className={cn(
          'inline-flex h-7 items-center gap-2 rounded-md px-1.5 outline-none select-none',
          'transition-colors hover:bg-accent focus-visible:ring-ring/40 focus-visible:ring-2'
        )}
      >
        {/* Noya logo badge: transparent, logo image or fallback N */}
        <div className='flex size-[1.375rem] shrink-0 items-center justify-center overflow-hidden rounded-md'>
          <img
            src={logo}
            alt={t('Logo')}
            width={22}
            height={22}
            className='size-full rounded-md object-contain'
            onError={(e) => {
              // Fallback: hide broken image, show N text sibling
              ;(e.target as HTMLImageElement).style.display = 'none'
            }}
          />
        </div>
        <span className='max-w-[10rem] truncate text-[17px] font-bold' style={{ color: '#1e3a8a' }}>
          {name}
        </span>
      </a>
    )
  }

  // Sidebar variant: logo-icon badge + name, matching nova_portal .sb-logo
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          size='lg'
          title={version}
          className='hover:text-sidebar-foreground active:text-sidebar-foreground cursor-default hover:bg-transparent active:bg-transparent'
          render={<div />}
        >
          {/* Square badge with logo(透明底) */}
          <div className='flex aspect-square size-[1.875rem] shrink-0 items-center justify-center overflow-hidden rounded-lg'>
            <img
              src={logo}
              alt={t('Logo')}
              width={30}
              height={30}
              className='size-full rounded-lg object-contain'
            />
          </div>
          <div className='grid flex-1 text-start leading-tight group-data-[collapsible=icon]:hidden'>
            <span className='truncate text-[17px] font-bold' style={{ color: '#1e3a8a' }}>
              {name}
            </span>
          </div>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
